import { Container, Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { fetchCompletedSessions } from '../../../../../actions/hub/hubSessionsPageActions';
import EnrolmentLayout from '../../../../../common/hub/EnrolmentLayout';
import { DatePicker, useDatePickerProps } from '../../../../../components/DatePicker/DatePicker';
import { EnrolmentBrief } from '../../../../../components/EnrolmentManagement/EnrolmentBrief';
import { Loader } from '../../../../../components/Loader';
import { useEnrolmentDetailQuery } from '../../../../../hooks/queries/useEnrolmentQuery';
import { useGetAvailableTimeSlotsOfTutor } from '../../../../../hooks/queries/useGetAvailability';
import { useGetSoonToExpireVouchers } from '../../../../../hooks/queries/useVoucherQuery';
import { getTimezone } from '../../../../../selectors/hubSelector';
import { ENROLMENT_STATUS } from '../../../../../types/hubEnums';
import { EnrolmentDetail, FormFlowProps, TutorTimeSlot } from '../../../../../types/hubInterfaces';
import { InitialState } from '../../../../../types/hubTypes';
import {
  DATE_FORMAT_TYPE_9,
  PAUSE_SESSIONS_PAYLOAD_DATE_FORMAT,
  SPECIAL_DATE_TYPE,
} from '../../../../../util/constants';
import { willEnrolmentBePausedCheck } from '../../../../../util/helpers';
import type { Params } from '../AddOneOffSession';
import { TimeSlots } from '../TimeSlots/TimeSlots';
import { VoucherMessage } from '../Voucher/VoucherMessage';
import { SessionAddedMessage } from './SessionAddedMessage';

interface Props {
  formFlowProps: FormFlowProps;
}

interface HistoryState {
  currentSelectedDate?: string;
  sessionWasAdded?: boolean;
  voucherWasUsed?: boolean;
  remainingVoucherCount?: number;
  enrolmentDetails?: EnrolmentDetail;
  tutorName?: string;
  timeSlot?: TutorTimeSlot;
  timezone?: string;
}

const DateAndTimeSelection = ({ formFlowProps }: Props) => {
  const { enrolmentId } = useParams<Params>();
  const dispatch = useDispatch();
  const history = useHistory<HistoryState>();
  const {
    location: { state },
  } = history;
  const {
    currentSelectedDate,
    sessionWasAdded,
    voucherWasUsed,
    remainingVoucherCount,
    enrolmentDetails: sessionEnrolmentDetails,
    tutorName: sessionTutorName,
    timeSlot: sessionTimeSlot,
    timezone: sessionTimezone,
  } = state ?? {};

  const dismissSessionAddedMessage = () => {
    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      state: {
        ...state,
        sessionWasAdded: false,
      },
    });
  };
  const studentTimeZone: string = useSelector(getTimezone);
  const schoolTermsPublicHolidays = useSelector(
    (state: InitialState) => state.hubSessionsPage.schoolTermsPublicHolidays
  );
  const upcomingSessions = useSelector((state: InitialState) => state.hubSessionsPage.upcomingSessions);
  const { data: enrolmentDetail, isLoading, error } = useEnrolmentDetailQuery({ enrolmentId });
  const {
    effectiveFromDate,
    enrolmentDiscipline,
    enrolmentPausedFrom,
    enrolmentPausedTo,
    enrolmentEndDate,
    enrolmentSchedule,
    enrolmentStatus,
    studentName,
    tutorName,
    tutorSfId,
    tutorPhoto,
    packageSfid,
  } = enrolmentDetail ?? {};

  const [showOtherTutors, setShowOtherTutors] = useState(true);
  const [selectedDateChanged, setSelectedDateChanged] = useState(false);
  const [selectedDateHasTimeSlots, setSelectedDateHasTimeSlots] = useState(false);

  const willEnrolmentBePaused = willEnrolmentBePausedCheck({ enrolmentPausedFrom, enrolmentPausedTo });

  const { data: soonToExpireVoucher } = useGetSoonToExpireVouchers();
  const voucherToBeUsed = soonToExpireVoucher?.[0] || undefined;
  const willUseVoucher = !!voucherToBeUsed;
  const minDate = moment().add(1, 'days').format(PAUSE_SESSIONS_PAYLOAD_DATE_FORMAT);

  const getMaxDate = () => {
    const cancelRequested = enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED;
    const isFinishing = enrolmentStatus === ENROLMENT_STATUS.FINISHING;
    const maxDate = () => {
      if (cancelRequested) {
        return effectiveFromDate;
      } else if (isFinishing) {
        return enrolmentEndDate;
      }
      return enrolmentPausedFrom;
    };

    if ((cancelRequested || willEnrolmentBePaused || isFinishing) && moment(maxDate()).isValid()) {
      return moment(maxDate()).subtract(1, 'day').format(PAUSE_SESSIONS_PAYLOAD_DATE_FORMAT);
    } else {
      /*
        User may only pick a date up to 4 weeks. 28 (+ 1) is needed
        so the 28th day is included
      */
      const fourWeeksLater = moment().add(29, 'days');
      const voucherExpiryDate = moment(voucherToBeUsed?.expiryDate);
      if (willUseVoucher && voucherExpiryDate.isBefore(fourWeeksLater)) {
        return voucherExpiryDate.format(PAUSE_SESSIONS_PAYLOAD_DATE_FORMAT);
      }
      return moment().add(29, 'days').format(PAUSE_SESSIONS_PAYLOAD_DATE_FORMAT);
    }
  };

  const datePickerProps = useDatePickerProps({
    defaultDate: currentSelectedDate ? [currentSelectedDate] : null,
    labelText: 'Date',
    placeholder: 'Select date',
    options: {
      maxDate: getMaxDate(),
      minDate,
      monthSelectorType: 'static',
      inline: true,
    },
  });
  const selectedDate = datePickerProps.value[0];

  /* 
    convert the "reschedule from" and "reschedule to" dates to midnight,
    for instance, if the chosen date is March 12, 2023
    
    rescheduleFrom: "2023-03-11 12:59:59"
    rescheduleTo: "2023-03-12 12:59:59"
  */
  const { data: tutorTimeSlots, isLoading: isTutorTimeSlotsLoading } = useGetAvailableTimeSlotsOfTutor(
    {
      tutorId: showOtherTutors ? undefined : tutorSfId,
      rescheduleFrom: moment(selectedDate)
        .tz(studentTimeZone)
        .startOf('day')
        .utc()
        .subtract(1, 'second')
        .format(DATE_FORMAT_TYPE_9),
      rescheduleTo: !!selectedDate
        ? moment(selectedDate)
            .add(1, 'day')
            .tz(studentTimeZone)
            .startOf('day')
            .utc()
            .subtract(1, 'second')
            .format(DATE_FORMAT_TYPE_9)
        : null,
      studentTimeZone,
      packageId: packageSfid,
    },
    (data) => {
      if (selectedDateChanged) {
        setSelectedDateHasTimeSlots(!isEmpty(data));
      }
      setSelectedDateChanged(false);
    }
  );

  useEffect(() => {
    dispatch(fetchCompletedSessions());
  }, [dispatch]);

  return (
    <EnrolmentLayout
      error={error}
      isLoading={isLoading}
      sectionHeading={`${studentName} ${enrolmentDiscipline}`}
      mainHeading="Add a session"
      mainHeadingClassName="merriweather-important !text-2xl lg:!text-[32px]"
      enrolmentId={enrolmentId}
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
    >
      <Container>
        {sessionWasAdded && sessionEnrolmentDetails && sessionTimeSlot && sessionTutorName && sessionTimezone && (
          <SessionAddedMessage
            tutorName={sessionTutorName}
            enrolmentDetails={sessionEnrolmentDetails}
            timeSlot={sessionTimeSlot}
            timezone={sessionTimezone}
            voucherWasUsed={voucherWasUsed}
            onDismiss={dismissSessionAddedMessage}
          />
        )}
        <VoucherMessage
          availableVoucherCount={voucherWasUsed ? remainingVoucherCount : undefined}
          body={<p>The voucher with the closest expiry date will be used to book this session</p>}
        />
        <EnrolmentBrief tutorName={tutorName} tutorPhoto={tutorPhoto} enrolmentSchedule={enrolmentSchedule} />
        <p className="text-xl font-bold">When would you like to add a session with {tutorName}?</p>
        <p className="mt-4 text-grey-5">
          Please note that we require 24 hours' notice to schedule an additional session and it must take place within
          the next 4 weeks.
        </p>
        <div className="mt-8 flex flex-col gap-10 lg:flex-row">
          <div className="w-full lg:max-w-xs">
            <p className="mb-3 text-[20px] font-bold">Select date</p>
            <DatePicker
              {...datePickerProps}
              inline={true}
              monthSelectorType="static"
              holidays={schoolTermsPublicHolidays.filter(
                (holiday) => holiday.type.toLowerCase() === SPECIAL_DATE_TYPE.PUBLIC_HOLIDAY.toLowerCase()
              )}
              upcomingSessions={upcomingSessions.filter((session) => session.student === studentName)}
              timezone={studentTimeZone}
              valueChangeCallback={(date) => setSelectedDateChanged(date !== selectedDate)}
            />
          </div>
          <div
            className={classNames(
              'w-full lg:max-w-xl',
              selectedDateHasTimeSlots || (isTutorTimeSlotsLoading && selectedDateChanged)
                ? 'self-start !border-0'
                : 'flex items-center justify-center rounded-lg border border-dashed border-grey-3 px-5 py-16 lg:p-4 lg:pt-0'
            )}
          >
            <Pending loading={isTutorTimeSlotsLoading && selectedDateChanged} loader={<Loader />}>
              {!!selectedDate ? (
                <TimeSlots
                  timeSlotCallback={(timeSlot) => {
                    formFlowProps.next({
                      currentSelectedDate: selectedDate,
                      enrolmentDetail,
                      timeSlot,
                      otherTutor: tutorSfId !== timeSlot.tutorsAvailable[0].tutorId,
                    });
                  }}
                  tutorName={tutorName}
                  tutorSfId={tutorSfId}
                  selectedDate={selectedDate}
                  timeSlots={tutorTimeSlots}
                  showOtherTutors={showOtherTutors}
                  setShowOtherTutors={setShowOtherTutors}
                  showHeaderSection={selectedDateHasTimeSlots}
                  isTutorTimeSlotsLoading={isTutorTimeSlotsLoading}
                />
              ) : (
                <p className="m-auto text-center text-lg font-bold">Select a date to see available session times</p>
              )}
            </Pending>
          </div>
        </div>
      </Container>
    </EnrolmentLayout>
  );
};

export { DateAndTimeSelection };
